import React, { useCallback, useMemo, useState } from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import * as XLSX from 'xlsx';
import { getCategories, deleteCategory } from '../../services/productServices';
import AddCategoryDialog from './AddCategoryDialog';
import ConfirmDialog from '../ui/ConfirmDialog';
import NotificationSnackbar from '../ui/NotificationSnackbar';
import { CrmButton, CrmTooltip } from '../ui';
import { CrmEntityListPage } from '../listing';
import useAutoRefresh from '../../hooks/useAutoRefresh';

const exportCategories = (rows, filename) => {
  const worksheet = XLSX.utils.json_to_sheet(rows.map((category) => ({
    Category: category.name,
    Products: category.product_count,
  })));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Categories');
  XLSX.writeFile(workbook, filename);
};

function CategoryList({ hideSavedViews = false, staticViews, activeStaticView, onStaticViewChange }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const showSnackbar = useCallback((message, severity = 'info') => setSnackbar({ open: true, message, severity }), []);

  const fetchCategories = useCallback(async ({ isAutoRefresh = false } = {}) => {
    if (!isAutoRefresh) setLoading(true);
    try {
      const data = await getCategories();
      const flat = [];
      const flatten = (nodes, path = '') => nodes.forEach((category) => {
        const label = path ? `${path} > ${category.name}` : category.name;
        flat.push({
          ...category,
          name: label,
          rawName: category.name,
          product_count: Number(category.product_count || 0),
        });
        if (category.children?.length) flatten(category.children, label);
      });
      flatten(Array.isArray(data) ? data : []);
      setCategories(flat);
    } catch {
      showSnackbar('Failed to load categories', 'error');
    } finally {
      if (!isAutoRefresh) setLoading(false);
    }
  }, [showSnackbar]);

  useAutoRefresh(fetchCategories, { intervalMs: 20000 });



  const confirmDelete = async () => {
    try {
      for (const id of deleteTarget) await deleteCategory(id);
      showSnackbar(`${deleteTarget.length} categor${deleteTarget.length === 1 ? 'y' : 'ies'} deleted`, 'success');
      await fetchCategories({ isAutoRefresh: true });
    } catch (error) {
      showSnackbar(error?.response?.data?.error || 'Failed to delete category', 'error');
    } finally {
      setConfirmOpen(false);
      setDeleteTarget([]);
    }
  };

  const columns = useMemo(() => [
    { id: 'name', label: 'Internal / ERP Category', width: 300, minWidth: 100, hideable: false, pinnable: true },
    { id: 'product_count', label: 'Products', width: 140, minWidth: 100, type: 'number', accessor: (category) => Number(category.product_count || 0) },
    {
      id: 'row_actions', label: 'Actions', width: 104, minWidth: 100,
      hideable: false, sortable: false, searchable: false, className: 'crm-data-table__actions-cell',
      renderCell: (category) => (
        <span className="crm-row-actions">
          <CrmTooltip content="Edit category"><CrmButton iconOnly size="small" variant="tertiary" className="crm-row-action" onClick={() => { setEditingCategory(category); setOpenDialog(true); }} aria-label="Edit category"><EditOutlinedIcon /></CrmButton></CrmTooltip>
          <CrmTooltip content="Delete category"><CrmButton iconOnly size="small" variant="tertiary" className="crm-row-action crm-row-action--danger" onClick={() => { setDeleteTarget([category.id]); setConfirmOpen(true); }} aria-label="Delete category"><DeleteOutlineIcon /></CrmButton></CrmTooltip>
        </span>
      ),
    },
  ], []);

  const filters = useMemo(() => [
    { id: 'name', columnId: 'name', label: 'Category', type: 'text', quick: true },
    { id: 'product_count', columnId: 'product_count', label: 'Products', type: 'text', quick: true },
  ], []);

  return (
    <div className="contacts-page">
      <CrmEntityListPage
        moduleKey="categories"
        title="Internal / ERP Categories"
        itemLabel="category"
        itemPluralLabel="categories"
        items={categories}
        columns={columns}
        filterDefinitions={filters}
        initialSort={{ columnId: 'name', direction: 'asc' }}
        loading={loading}
        onRefresh={() => fetchCategories()}
        onCreate={() => { setEditingCategory(null); setOpenDialog(true); }}
        createLabel="Add internal category"
        onExport={(rows) => exportCategories(rows, 'categories.xlsx')}
        onExportSelected={(rows) => exportCategories(rows, 'selected_categories.xlsx')}
        onDeleteSelected={(ids) => { setDeleteTarget(ids); setConfirmOpen(true); }}
        hideSavedViews={hideSavedViews}
        staticViews={staticViews}
        activeStaticView={activeStaticView}
        onStaticViewChange={onStaticViewChange}
      />
      <AddCategoryDialog open={openDialog} category={editingCategory} onClose={() => { setOpenDialog(false); fetchCategories({ isAutoRefresh: true }); }} />
      <ConfirmDialog open={confirmOpen} title="Delete category?" message={`Delete ${deleteTarget.length} categor${deleteTarget.length === 1 ? 'y' : 'ies'}? This cannot be undone.`} confirmText="Delete" onCancel={() => setConfirmOpen(false)} onConfirm={confirmDelete} />
      <NotificationSnackbar {...snackbar} onClose={() => setSnackbar((previous) => ({ ...previous, open: false }))} />
    </div>
  );
}

export default CategoryList;
